import { defaultLocalePath } from '~/constants/appLocale'

/**
 * The marketing site's feature subpages.
 *
 * The homepage used to be a single long page and the navbar dropdown only
 * scrolled to anchors on it, so nothing was linkable or indexable on its own.
 * This list is the one place that maps a feature to its page; the navbar
 * dropdown, the homepage grid and the cross-links at the bottom of each
 * subpage all read from it.
 */
export interface FeaturePage {
  id: string
  path: string
  titleKey: string
  descriptionKey: string
  /** Bootstrap icon class, as used by the navbar dropdown. */
  icon: string
  /**
   * The template's own illustrated icon, used on the big cards. The line art is
   * dark, so it always sits on a light tile, in dark mode too.
   */
  illustration: string
}

export const FEATURE_PAGES: FeaturePage[] = [
  {
    id: 'cv-builder',
    path: '/free-cv-builder',
    titleKey: 'marketing.features.cvBuilder.title',
    descriptionKey: 'marketing.features.cvBuilder.description',
    icon: 'bi-file-earmark-text',
    illustration: '/sofax/service-icon-1.svg',
  },
  {
    id: 'templates',
    path: '/templates',
    titleKey: 'marketing.features.templates.title',
    descriptionKey: 'marketing.features.templates.description',
    icon: 'bi-collection',
    illustration: '/sofax/content-icon-3.svg',
  },
  {
    id: 'ai-features',
    path: '/ai-features',
    titleKey: 'marketing.features.aiFeatures.title',
    descriptionKey: 'marketing.features.aiFeatures.description',
    icon: 'bi-robot',
    illustration: '/sofax/content-icon-2.svg',
  },
  {
    id: 'ats-check',
    path: '/ats-check',
    titleKey: 'marketing.features.atsCheck.title',
    descriptionKey: 'marketing.features.atsCheck.description',
    icon: 'bi-shield-check',
    illustration: '/sofax/feature-icon-2.svg',
  },
  {
    id: 'tailor',
    path: '/tailor-resume-to-job',
    titleKey: 'marketing.features.tailor.title',
    descriptionKey: 'marketing.features.tailor.description',
    icon: 'bi-bullseye',
    illustration: '/sofax/service-icon-2.svg',
  },
  {
    id: 'tracker',
    path: '/job-application-tracker',
    titleKey: 'marketing.features.tracker.title',
    descriptionKey: 'marketing.features.tracker.description',
    icon: 'bi-kanban',
    illustration: '/sofax/feature-icon-3.svg',
  },
  {
    id: 'cover-letter',
    path: '/ai-cover-letter',
    titleKey: 'marketing.features.coverLetter.title',
    descriptionKey: 'marketing.features.coverLetter.description',
    icon: 'bi-envelope-paper',
    illustration: '/sofax/contact-icon-3.svg',
  },
  {
    id: 'online-cv',
    path: '/online-cv',
    titleKey: 'marketing.features.onlineCv.title',
    descriptionKey: 'marketing.features.onlineCv.description',
    icon: 'bi-share',
    illustration: '/sofax/content-icon-1.svg',
  },
  {
    id: 'analytics',
    path: '/cv-analytics',
    titleKey: 'marketing.features.analytics.title',
    descriptionKey: 'marketing.features.analytics.description',
    icon: 'bi-graph-up',
    illustration: '/sofax/service-icon-3.svg',
  },
  {
    id: 'languages',
    path: '/cv-languages',
    titleKey: 'marketing.features.languages.title',
    descriptionKey: 'marketing.features.languages.description',
    icon: 'bi-translate',
    illustration: '/sofax/contact-icon-1.svg',
  },
]

/** Every marketing route that must render without a session. */
export const FEATURE_PAGE_PATHS = [...FEATURE_PAGES.map(page => page.path), '/pricing']

/**
 * Pages that sell the product rather than run it.
 *
 * The navbar shows its marketing menu here even for signed-in visitors; without
 * this the feature pages lost their navigation the moment someone logged in.
 */
export function isMarketingPath(localizedPath: string): boolean {
  const path = defaultLocalePath(localizedPath)
  if (path === '/' || path.startsWith('/#')) return true
  if (path === '/demo' || path === '/blog' || path.startsWith('/blog/')) return true
  return FEATURE_PAGE_PATHS.includes(path)
}
